import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, CandlestickData, HistogramData, Time, ColorType } from 'lightweight-charts';
import { getApiUrl } from '../../config/api';

const TIMEFRAMES = [
  { label: '1m', interval: '1m', wsStream: 'tonusdt@kline_1m', limit: 500 },
  { label: '5m', interval: '5m', wsStream: 'tonusdt@kline_5m', limit: 500 },
  { label: '15m', interval: '15m', wsStream: 'tonusdt@kline_15m', limit: 500 },
  { label: '1H', interval: '1h', wsStream: 'tonusdt@kline_1h', limit: 500 },
  { label: '4H', interval: '4h', wsStream: 'tonusdt@kline_4h', limit: 500 },
  { label: '1D', interval: '1d', wsStream: 'tonusdt@kline_1d', limit: 365 },
];

const INTERVAL_SEC: Record<string, number> = {
  '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400,
};
const HOURS_MAP: Record<string, number> = {
  '1m': 8, '5m': 24, '15m': 48, '1h': 168, '4h': 720, '1d': 2160,
};

const PressureChart: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candleRef = useRef<any>(null);
  const histRef = useRef<any>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [tfIdx, setTfIdx] = useState(0);
  const [connected, setConnected] = useState(false);
  const [latestScore, setLatestScore] = useState<number | null>(null);

  const tf = TIMEFRAMES[tfIdx];

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: 'rgba(255,255,255,0.1)' },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.1)',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: true,
      handleScale: true,
    });

    // Pane 0: Candles (v5: addSeries(Type, options, paneIndex))
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
      priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
    }, 0);

    // Pane 1: Score histogram — separate panel below candles
    const hist = chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: 'custom',
        formatter: (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
      },
      base: 0,
      title: 'Pressure Score',
    }, 1);

    chartRef.current = chart;
    candleRef.current = candle;
    histRef.current = hist;

    const onResize = () => {
      if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); chart.remove(); };
  }, []);

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        const kResp = await fetch(`https://api.binance.com/api/v3/klines?symbol=TONUSDT&interval=${tf.interval}&limit=${tf.limit}`);
        if (kResp.ok) {
          const klines = await kResp.json();
          candleRef.current?.setData(klines.map((k: any[]) => ({
            time: (k[0] / 1000) as Time,
            open: parseFloat(k[1]), high: parseFloat(k[2]),
            low: parseFloat(k[3]), close: parseFloat(k[4]),
          })));
        }

        const snap = INTERVAL_SEC[tf.interval] || 60;
        const sResp = await fetch(getApiUrl(`/api/pressure/score/TON/history?hours=${HOURS_MAP[tf.interval] || 24}`), { credentials: 'include' });
        if (sResp.ok) {
          const scores = await sResp.json();
          const buckets: Record<number, number[]> = {};
          for (const s of scores) {
            const b = Math.floor(new Date(s.ts).getTime() / 1000 / snap) * snap;
            (buckets[b] ||= []).push(s.score_smoothed);
          }
          const data: HistogramData[] = Object.entries(buckets)
            .map(([ts, vals]) => {
              const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
              return {
                time: +ts as Time, value: avg,
                color: avg >= 0
                  ? `rgba(74,222,128,${Math.min(0.3 + Math.abs(avg) / 100 * 0.7, 1)})`
                  : `rgba(248,113,113,${Math.min(0.3 + Math.abs(avg) / 100 * 0.7, 1)})`,
              };
            }).sort((a, b) => (a.time as number) - (b.time as number));
          histRef.current?.setData(data);
          if (scores.length) setLatestScore(scores[scores.length - 1].score_smoothed);
        }

        chartRef.current?.timeScale().fitContent();
      } catch (e) { console.error(e); }
    };
    load();
  }, [tf]);

  // WebSocket
  useEffect(() => {
    wsRef.current?.close();
    const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${tf.wsStream}`);
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      const k = JSON.parse(e.data).k;
      if (k) candleRef.current?.update({
        time: (k.t / 1000) as Time, open: +k.o, high: +k.h, low: +k.l, close: +k.c,
      });
    };
    wsRef.current = ws;
    return () => { ws.close(); };
  }, [tf]);

  // Poll score
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(getApiUrl('/api/pressure/score/TON'), { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (d.ts) {
            const v = d.score_smoothed, snap = INTERVAL_SEC[tf.interval] || 60;
            histRef.current?.update({
              time: (Math.floor(d.ts / snap) * snap) as Time, value: v,
              color: v >= 0
                ? `rgba(74,222,128,${Math.min(0.3 + Math.abs(v) / 100 * 0.7, 1)})`
                : `rgba(248,113,113,${Math.min(0.3 + Math.abs(v) / 100 * 0.7, 1)})`,
            });
            setLatestScore(v);
          }
        }
      } catch {}
    };
    const i = setInterval(poll, 60_000);
    return () => clearInterval(i);
  }, [tf]);

  const sc = latestScore === null ? '#f59e0b' : latestScore >= 0 ? '#4ade80' : '#f87171';

  return (
    <div className="glass-card p-2 sm:p-4">
      {/* Header — stacks on mobile */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold text-viz-text-primary">TON/USDT</h2>
          <span className="text-[10px] sm:text-xs text-viz-text-secondary">{tf.label}</span>
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
          {latestScore !== null && (
            <div className="px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-mono" style={{ backgroundColor: `${sc}20`, color: sc }}>
              {latestScore > 0 ? '+' : ''}{latestScore.toFixed(1)}
            </div>
          )}
        </div>
        <div className="flex gap-0.5 sm:gap-1 ml-auto">
          {TIMEFRAMES.map((t, i) => (
            <button key={t.interval} onClick={() => setTfIdx(i)}
              className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded ${tfIdx === i ? 'bg-viz-primary text-white' : 'bg-viz-bg-tertiary text-viz-text-secondary'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} className="h-[350px] sm:h-[500px]" />
    </div>
  );
};

export default PressureChart;
